import type {
  Pagination,
  PlayerTransfer,
  PlayerTransferRelatedTransferResponseRelatedPagedData,
} from '@on3/api';
import type { ErrorData } from '@on3/ui-lib/api/schema/custom-contracts';
import { externalApi, useAuth, useSite } from '@on3/ui-lib/index';
import capitalizeFirstLetter from '@on3/ui-lib/utils/capitalizeFirstLetter';
import { type AxiosError } from 'axios';
import clsx from 'clsx';
import { PremiumBlock } from 'components/Db/Profile/PremiumBlock/PremiumBlock';
import { TransferStatus } from 'components/RecruitmentStatus/TransferStatus';
import { PrimaryButton } from 'components/UI/Buttons/PrimaryButton';
import { Text } from 'components/UI/Typography/Text';
import { useRouter } from 'next/router';
import { OrderByType } from 'pages/transfer-portal/[transferType]/[sport]/[[...year]]';
import { useCallback, useState } from 'react';

import { IndustryComparisonFourServicesItem } from './IndustryComparisonFourServicesItem';
import styles from './IndustryComparisonTp.module.scss';
import { IndustryComparisonTpColumnHeaders } from './IndustryComparisonTpColumnHeaders';
import { IndustryComparisonTpPlayerItem } from './IndustryComparisonTpPlayerItem';

type SelectedOptions =
  | 'Consensus'
  | 'On3'
  | 'Rivals'
  | 'Industry'
  | '247'
  | 'Espn';

interface IndustryComparisonTpListProps {
  playerData?: PlayerTransferRelatedTransferResponseRelatedPagedData | null;
  pagination?: Pagination;
  isTop: boolean;
}

export const IndustryComparisonTpList = ({
  playerData,
  pagination: currentPagination,
  isTop,
}: IndustryComparisonTpListProps) => {
  const router = useRouter();
  const { status, orderby, transferType } = router.query;
  const { currentSite } = useSite();
  const { user } = useAuth();
  const { list: playerList, relatedModel } = playerData || {};
  const { year, sport, position } = relatedModel || {};
  const [list, setList] = useState<PlayerTransfer[]>(playerList || []);
  const [pagination, setPagination] = useState<Pagination | undefined>(
    currentPagination,
  );
  const { currentPage, pageCount } = pagination || {};
  const [disable, setDisable] = useState(false);
  const [pageNumber, setPageNumber] = useState(2);
  const page = pageNumber;
  const capitalizeStatus = status ? capitalizeFirstLetter(`${status}`) : null;

  const [selected, setSelected] = useState<SelectedOptions | string>(
    isTop ? 'index' : 'status',
  );
  const [ascending, setAscending] = useState(isTop ? false : true);
  const [disabled, setDisabled] = useState(false);
  const listType = isTop ? 'best-available' : 'latest';
  const utm_campaign = isTop
    ? 'industry-comparison-top'
    : 'industry-comparison-wire';
  const sortList = useCallback(
    (
      incomingList: PlayerTransfer[],
      sortBy: SelectedOptions | string,
      sortDirection: boolean,
    ) => {
      if (!incomingList?.length) {
        return incomingList;
      }

      const isPortalRank = sortBy === 'index';
      const isPlayer = sortBy === 'name';
      const isStatus = sortBy === 'status';
      const sortedList = [...incomingList]?.sort((a, b) => {
        const setIndex =
          a.transferIndustryComparison?.findIndex((i) => i.type === sortBy) ||
          0;
        let setA = null;
        let setB = null;

        if (isPlayer) {
          setA = a.name;
          setB = b.name;
        } else if (isPortalRank) {
          setA = a.index;
          setB = b.index;
        } else if (isStatus) {
          setA = a.commitStatus?.date;
          setB = b.commitStatus?.date;
        } else {
          const setAobject = a.transferIndustryComparison?.[setIndex];
          const setBobject = b.transferIndustryComparison?.[setIndex];

          setA = setAobject?.overallRank;
          setB = setBobject?.overallRank;
        }

        // puts nulls at the end of list
        if (!setA && setA !== 0) {
          return 1;
        }

        if (!setB && setB !== 0) {
          return -1;
        }

        if (setA < setB) {
          return sortDirection ? 1 : -1;
        }

        if (setA > setB) {
          return sortDirection ? -1 : 1;
        }

        return 0;
      });

      return sortedList;
    },
    [],
  );

  const handleClick = useCallback(async () => {
    setDisable(true);

    const sportKey = sport?.key;
    const sortBy = isTop ? 'index' : 'status';
    const isAscending = isTop ? false : true;
    const params = {
      sportKey,
      year: `${year}`,
      positionAbbr: position?.abbr,
      orderBy: orderby || OrderByType[transferType as keyof typeof OrderByType],
      orgKey: currentSite?.organizationKey,
      status: capitalizeStatus?.includes('Best') ? 'Entered' : capitalizeStatus,
      page,
    };

    try {
      const response = await externalApi.get(
        `/public/rdb/v1/transfers/${listType}/`,
        {
          params,
        },
      );

      const newList = [...list, ...response.data.list];

      const newSortedList = sortList(newList, sortBy, isAscending);

      setList(newSortedList);
      setSelected(sortBy);
      setPagination(response.data.pagination);
      setPageNumber(pageNumber + 1);
    } catch (e: unknown) {
      const { response, message }: AxiosError<ErrorData> =
        e as AxiosError<ErrorData>;

      console.error(
        'DB TranferPortal Error: ',
        response?.data?.message || message,
      );
    } finally {
      setDisable(false);
    }
  }, [
    capitalizeStatus,
    currentSite?.organizationKey,
    isTop,
    list,
    listType,
    orderby,
    page,
    pageNumber,
    position,
    sortList,
    sport,
    transferType,
    year,
  ]);

  const showLoadMoreButton =
    currentPage && pageCount && list?.length > 49 && currentPage < pageCount;

  const handleChange = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      setDisabled(true);
      const { dataset } = e.currentTarget;
      const { sorttype, flip = 'false' } = dataset;
      const sorttypeKey = sorttype as SelectedOptions | string;
      const isFlipped = flip === 'true';
      let orderBy = ascending;

      if (sorttypeKey !== selected) {
        setSelected(sorttypeKey);
        orderBy = isFlipped;
        setAscending(isFlipped);
      } else {
        orderBy = !ascending;
        setAscending((prev) => !prev);
      }

      const sortedList = sortList(list, sorttypeKey, orderBy);

      sortedList && setList(sortedList);
      setDisabled(false);
    },
    [ascending, selected, sortList, list],
  );

  return (
    <>
      {!!list?.length && (
        <IndustryComparisonTpColumnHeaders
          ascending={ascending}
          disabled={disabled}
          onChange={handleChange}
          selected={selected}
          showRank={isTop}
          showStatus
          transferPortal
          useRank={isTop}
        />
      )}
      <ol className={styles.listWrapper}>
        {!!list?.length &&
          list?.map((item, index) => {
            const displayTransferData = item.transferIndustryComparison?.filter(
              (el) => el.type !== 'Espn',
            );
            const showRatings = index < 5 || user?.has;

            return (
              <li
                className={clsx(styles.playerItemContainer, {
                  [styles.showRank]: isTop,
                })}
                key={`${item.key}-${index}-${ascending}-${selected}-industry-comparison`}
              >
                <IndustryComparisonTpPlayerItem
                  className={styles.playerItem}
                  person={item}
                  selected={selected === 'Industry'}
                  showRank={isTop}
                />
                {showRatings ? (
                  <IndustryComparisonFourServicesItem
                    className={styles.ratingsItem}
                    psoKey={item.key}
                    ratings={displayTransferData}
                    selected={selected}
                    showJerseyOrRank={isTop}
                    slug={item.slug}
                  />
                ) : (
                  <PremiumBlock
                    className={styles.membersOnly}
                    utmCampaign={utm_campaign}
                    utmContent="lock"
                    utmMedium="web"
                    utmSource="transfer_portal"
                  />
                )}
                {item.commitStatus && (
                  <TransferStatus
                    {...item.commitStatus}
                    className={styles.playerStatus}
                    lastTeam={item.lastTeam}
                    predictions={item.predictions}
                    recruitmentKey={item.recruitmentKey || 0}
                    slug={item.slug}
                    year={relatedModel?.year}
                  />
                )}
              </li>
            );
          })}
        {!list?.length && (
          <Text className={styles.noListText} component="li">
            There are no players for this selection.
          </Text>
        )}
      </ol>
      {showLoadMoreButton && (
        <PrimaryButton
          className={styles.btnLoadMore}
          disabled={disable}
          fullWidth={false}
          onClick={handleClick}
          variant="text"
        >
          Load More
        </PrimaryButton>
      )}
    </>
  );
};
